import { useEffect, useState } from 'react';
import {
  Claim,
  RelatedClaim,
  ReinstatementScope,
  ToggleReinstatementResponse,
  getRelatedClaims,
  toggleReinstatement,
} from '../api/claims';

interface Props {
  claim: Claim;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (result: ToggleReinstatementResponse) => void;
}

const ACTIVE_RADIO_STYLE: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  alignItems: 'flex-start',
  padding: '10px 12px',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  cursor: 'pointer',
  marginBottom: 8,
};

export default function ReinstatementScopeModal({ claim, isOpen, onClose, onSuccess }: Props) {
  const currentlyDisabled = !!claim.reinstatementDisabled;
  const action: 'enable' | 'disable' = currentlyDisabled ? 'enable' : 'disable';

  const [scope, setScope] = useState<ReinstatementScope>('all');
  const [relatedClaims, setRelatedClaims] = useState<RelatedClaim[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set([claim.id]));
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [relatedError, setRelatedError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reset state when modal opens/closes or the target claim changes.
  useEffect(() => {
    if (isOpen) {
      setScope('all');
      setSelectedIds(new Set([claim.id]));
      setSubmitError(null);
      setRelatedError(null);
    }
  }, [isOpen, claim.id]);

  // Fetch related claims once when the modal opens (used for both the "all" count
  // and the "selected" checklist).
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoadingRelated(true);
    setRelatedError(null);
    getRelatedClaims(claim.id)
      .then(res => {
        if (cancelled) return;
        setRelatedClaims(res.claims || []);
      })
      .catch(err => {
        if (cancelled) return;
        setRelatedError(err?.response?.data?.error || err.message || 'Failed to load related claims');
      })
      .finally(() => {
        if (!cancelled) setLoadingRelated(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, claim.id]);

  if (!isOpen) return null;

  const totalRelated = relatedClaims.length;

  function toggleSelected(id: string) {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  }

  async function handleConfirm() {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const disable = !currentlyDisabled;
      let result: ToggleReinstatementResponse;
      if (scope === 'selected') {
        if (selectedIds.size === 0) {
          setSubmitError('Pick at least one claim');
          setSubmitting(false);
          return;
        }
        result = await toggleReinstatement(claim.id, disable, {
          scope: 'selected',
          claimIds: Array.from(selectedIds),
        });
      } else {
        result = await toggleReinstatement(claim.id, disable, { scope });
      }
      onSuccess(result);
      onClose();
    } catch (err: any) {
      setSubmitError(err?.response?.data?.error || err.message || 'Failed to update reinstatement');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: 'white', borderRadius: 8, padding: 20,
          width: 'min(520px, 92vw)', maxHeight: '85vh', overflow: 'auto',
          boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
        }}
      >
        <h3 style={{ margin: '0 0 12px', fontSize: 18 }}>
          {action === 'disable' ? 'Disable' : 'Enable'} reinstatement
        </h3>
        <p style={{ margin: '0 0 16px', fontSize: 14, color: '#4b5563' }}>
          Which claims should this apply to?
        </p>

        <label style={ACTIVE_RADIO_STYLE}>
          <input
            type="radio"
            name="reinstate-scope"
            value="single"
            checked={scope === 'single'}
            onChange={() => setScope('single')}
            style={{ marginTop: 3 }}
          />
          <span>
            <strong>Just this claim</strong>
            <div style={{ fontSize: 12, color: '#6b7280' }}>{claim.id}</div>
          </span>
        </label>

        <label style={ACTIVE_RADIO_STYLE}>
          <input
            type="radio"
            name="reinstate-scope"
            value="all"
            checked={scope === 'all'}
            onChange={() => setScope('all')}
            style={{ marginTop: 3 }}
          />
          <span>
            <strong>All claims for this client</strong>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              {loadingRelated ? 'Counting…' : `${totalRelated} claim${totalRelated === 1 ? '' : 's'} in total`}
            </div>
          </span>
        </label>

        <label style={ACTIVE_RADIO_STYLE}>
          <input
            type="radio"
            name="reinstate-scope"
            value="selected"
            checked={scope === 'selected'}
            onChange={() => setScope('selected')}
            style={{ marginTop: 3 }}
          />
          <span style={{ width: '100%' }}>
            <strong>Choose specific claims</strong>
            <div style={{ fontSize: 12, color: '#6b7280' }}>Tick the claims you want included</div>
          </span>
        </label>

        {scope === 'selected' && (
          <div style={{
            border: '1px solid #e5e7eb', borderRadius: 6, padding: 8,
            margin: '4px 0 12px', maxHeight: 240, overflow: 'auto',
          }}>
            {loadingRelated && <div style={{ padding: 8, fontSize: 13 }}>Loading claims…</div>}
            {relatedError && (
              <div style={{ padding: 8, fontSize: 13, color: '#b91c1c' }}>{relatedError}</div>
            )}
            {!loadingRelated && !relatedError && relatedClaims.length === 0 && (
              <div style={{ padding: 8, fontSize: 13, color: '#6b7280' }}>No related claims found.</div>
            )}
            {relatedClaims.map(rc => {
              const checked = selectedIds.has(rc.id);
              return (
                <label key={rc.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 8px', cursor: 'pointer',
                  borderBottom: '1px solid #f3f4f6',
                }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleSelected(rc.id)}
                  />
                  <span style={{ fontSize: 13 }}>
                    <strong>{rc.id}</strong>
                    {rc.craFinanceLender && <span style={{ color: '#6b7280' }}> — {rc.craFinanceLender}</span>}
                    {rc.status && <span style={{ color: '#9ca3af' }}> · {rc.status}</span>}
                  </span>
                </label>
              );
            })}
          </div>
        )}

        {submitError && (
          <div style={{
            padding: 8, marginBottom: 12, borderRadius: 4,
            background: '#fef2f2', color: '#b91c1c', fontSize: 13,
          }}>
            {submitError}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={{
              padding: '8px 14px', border: '1px solid #d1d5db', background: 'white',
              borderRadius: 6, cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting || loadingRelated}
            style={{
              padding: '8px 14px', border: 'none',
              background: action === 'disable' ? '#dc2626' : '#16a34a',
              color: 'white', borderRadius: 6, cursor: submitting ? 'wait' : 'pointer',
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? 'Saving…' : (action === 'disable' ? 'Disable reinstatement' : 'Enable reinstatement')}
          </button>
        </div>
      </div>
    </div>
  );
}
